/* eslint-disable no-return-assign */
/* eslint-disable class-methods-use-this */
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { StateController } from "../state";
import { arrowDown } from "../svg";
import type { Filter, Option } from "./entities";

type OptionGroup = {
  major: Option;
  minors?: Array<Option>;
};

@customElement("umb-market-version-filter")
export class UmbMarketVersionFilter extends LitElement {
  @property({ type: Object })
  filter?: Filter;

  @state()
  options?: Array<OptionGroup> = [];

  @state()
  activeMinors?: string;

  #stateController: StateController;

  constructor() {
    super();

    this.#stateController = new StateController(this);
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.#groupOptions();
  }

  #groupOptions() {
    this.options = this.filter?.options
      .filter((o) => !o.value || o.name.startsWith("Version"))
      ?.map((major) => {
        if (!major.value) {
          return { major };
        }

        const majorNumber = major.value.split(".").at(0)!;
        const minors =
          this.filter?.options.filter(
            (o) =>
              o.value.startsWith(majorNumber) && !o.name.startsWith("Version")
          ) ?? [];

        minors
          .sort((a, b) =>
            a.value.localeCompare(b.value, undefined, { numeric: true })
          )
          .reverse();

        return {
          major,
          minors,
        };
      });
  }

  #filterChange(e: any) {
    this.#stateController.setFilter(this.filter!.alias, e.target.value);
  }

  #renderMinors(minors: Array<Option>) {
    return minors.map(
      (minor) => html`<uui-radio
        label="${minor.name}"
        value="${minor.value}"
        ?checked=${minor.selected || false}
      ></uui-radio>`
    );
  }

  #renderOptionGroup(optionGroup: OptionGroup) {
    return html`
      <div
        class="version"
        active=${this.activeMinors === optionGroup.major.name}
      >
        <div class="major">
          <uui-radio
            label="${optionGroup.major.name}"
            value="${optionGroup.major.value}"
            ?checked=${optionGroup.major.selected || false}
          ></uui-radio>
          ${when(
            optionGroup.minors?.length,
            () => html` <umb-market-button
              @click=${() => {
                this.activeMinors =
                  this.activeMinors === optionGroup.major.name
                    ? undefined
                    : optionGroup.major.name;
              }}
            >
              ${arrowDown}
            </umb-market-button>`
          )}
        </div>

        ${when(
          optionGroup.minors?.length,
          () =>
            html`<div class="minors">
              ${this.#renderMinors(optionGroup.minors!)}
            </div>`
        )}
      </div>
    `;
  }

  render() {
    return html`<uui-radio-group
      .name=${this.filter?.alias}
      @change=${this.#filterChange}
      >${this.options?.map((o) => this.#renderOptionGroup(o))}</uui-radio-group
    >`;
  }

  static styles = [
    css`
      uui-radio {
        --uui-radio-button-size: 24px;
        position: relative;
      }

      uui-radio-group {
        width: 100%;
      }
      uui-radio-group > div:first-child {
        margin-bottom: 3px;
      }

      .version {
        position: relative;
      }

      .version[active="true"]::before {
        content: "";
        border-left-width: 1px;
        border-left-color: #c2c2c2;
        border-left-style: solid;
        position: absolute;
        left: 11px;
        top: 24px;
        bottom: 11px;
        width: 1px;
        box-sizing: border-box;
        opacity: 0.5;
      }

      .version[active="true"] .minors uui-radio::before {
        content: "";
        border-bottom-width: 1px;
        border-bottom-color: #c2c2c2;
        border-bottom-style: solid;
        position: absolute;
        left: -12px;
        bottom: 11px;
        width: 12px;
        box-sizing: border-box;
        opacity: 0.5;
      }

      .major {
        display: flex;
        justify-content: space-between;
        align-items: center;
        max-height: 30px;
      }

      .major uui-radio {
        margin: 0;
      }

      .minors {
        display: none;
        margin: 3px 0 3px 24px;
      }

      [active="true"] .minors {
        display: block;
      }

      umb-market-button {
        --button-padding: 2px 4px;
        --button-border-radius: 100px;
        --button-width: auto;
      }

      svg {
        stroke-width: 1;
        --fill: #949494;
      }

      [active="true"] svg,
      umb-market-button:hover svg {
        --fill: var(--uui-color-default);
      }

      [active="true"] svg {
        transform: rotate(180deg);
      }
    `,
  ];
}
